import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { Heart, Plus } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'
import { FavoriteButton } from '@/features/favorites/ui'
import { useAddToCart } from '@/features/cart/api/use-cart'
import { ProductWeightSelectorSheet } from '@/widgets/product-weight-selector-sheet'
import { useCartNotification } from '@/widgets/cart-notification'
import { formatPrice, formatWeight, getWeightOptions } from '@/shared/lib/format'
import { calculateTeaPrice } from '@/shared/lib/pricing'
import { OptimizedImage } from '@/shared/ui'
import type { Tea } from '@/entities/product'

export interface RecommendationCardProps {
  tea: Tea
  index?: number
}

/**
 * Компактная карточка для горизонтального свайпа рекомендаций.
 * 
 * Размер: ~140-150px ширины, оптимизирована для свайпа.
 * Включает: фото, название, категория, цена, кнопка +.
 */
export function RecommendationCard({ tea, index = 0 }: RecommendationCardProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const addToCart = useAddToCart()
  const cartNotification = useCartNotification()
  
  const [isWeightSheetOpen, setIsWeightSheetOpen] = useState(false)
  
  const mainImage = tea.images?.[0]?.url || '/placeholder-product.svg'
  const pricePerGram = tea.priceAmount || 0
  
  const handleCardClick = () => {
    hapticFeedback.impactOccurred('light')
    navigate({
      to: '/product/$productId',
      params: { productId: tea.id },
    })
  }
  
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    hapticFeedback.impactOccurred('medium')
    setIsWeightSheetOpen(true)
  }
  
  const handleAddToCartWithWeight = (weight: number) => {
    addToCart.mutate({
      productType: 'tea',
      productId: tea.id,
      quantity: 1,
      weight,
    }, {
      onSuccess: (result) => {
        cartNotification.show({
          productName: `${tea.name} · ${formatWeight(weight)}`,
          productImage: tea.images?.[0]?.url,
          cartItemId: result.data?.id,
        })
        hapticFeedback.notificationOccurred('success')
      },
      onError: () => {
        hapticFeedback.notificationOccurred('error')
      }
    })
  }
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileTap={{ scale: 0.97 }}
        onClick={handleCardClick}
        className={cn(
          'flex-shrink-0 w-[140px] sm:w-[160px]',
          'bg-white dark:bg-dark-card rounded-2xl overflow-hidden',
          'shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.25)]',
          'border border-gray-100 dark:border-dark-border',
          'cursor-pointer active:shadow-lg transition-shadow'
        )}
      >
        {/* Фото */}
        <div className="relative aspect-square bg-gray-50 dark:bg-dark-elevated overflow-hidden">
          <OptimizedImage
            src={mainImage}
            alt={tea.name}
            preset="card"
            wrapperClassName="h-full w-full"
            className="object-cover"
            loading="lazy"
            decoding="async"
          />
          
          {/* Градиент */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
          
          {/* Кнопка избранного */}
          <div className="absolute top-1.5 right-1.5">
            <FavoriteButton
              productId={tea.id}
              variant="card"
              size="sm"
              animated
            />
          </div>
          
          {/* Бейдж "Новинка" */}
          {tea.isNew && (
            <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-primary/90 dark:bg-primary-dark/90 backdrop-blur-sm rounded-md text-[10px] font-semibold text-white">
              NEW
            </div>
          )}
          
          {/* Бейдж скидки */}
          {tea.isOnSale && !tea.isNew && (
            <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-red-500/90 backdrop-blur-sm rounded-md text-[10px] font-semibold text-white">
              SALE
            </div>
          )}
        </div>
        
        {/* Контент */}
        <div className="p-2.5 space-y-1.5">
          {/* Название — строго 2 строки с обрезкой */}
          <h3 className="text-xs font-semibold text-ink dark:text-white line-clamp-2 leading-snug h-[2.5rem] overflow-hidden">
            {tea.name}
          </h3>
          
          {/* Категория */}
          <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
            {tea.categoryName || 'Чай'}
          </p>
          
          {/* Цена и кнопка */}
          <div className="flex items-center justify-between gap-1 pt-1">
            <div className="flex-1 min-w-0">
              {tea.isOnSale && tea.salePriceAmount ? (
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-primary dark:text-primary-light">
                    от {formatPrice((tea.salePriceAmount || 0) * 10)}
                  </span>
                  <span className="text-[9px] text-gray-400 line-through">
                    {formatPrice((tea.priceAmount || 0) * 10)}
                  </span>
                </div>
              ) : (
                <span className="text-xs font-bold text-primary dark:text-primary-light">
                  от {formatPrice((tea.priceAmount || 0) * 10)}
                </span>
              )}
              <span className="text-[9px] text-gray-500 dark:text-gray-400 block mt-0.5">
                за 10г
              </span>
            </div>
            
            {/* Кнопка добавления */}
            <button
              type="button"
              onClick={handleAddClick}
              disabled={!tea.isInStock}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center',
                'bg-primary dark:bg-primary-dark text-white',
                'active:scale-90 transition-transform',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'shadow-sm'
              )}
              aria-label="В корзину"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
      
      {/* Bottom Sheet: Выбор веса */}
      <ProductWeightSelectorSheet
        isOpen={isWeightSheetOpen}
        onClose={() => setIsWeightSheetOpen(false)}
        weights={getWeightOptions(tea.isCakeAvailable ?? false, tea.isCakeOnly, tea.cakeWeight).map(weight => ({
          weight,
          price: calculateTeaPrice({ pricePerGram, weight, quantity: 1, cakePriceAmount: tea.cakePriceAmount, cakeWeight: tea.cakeWeight }).unitPrice,
          available: true,
        }))}
        cakeWeight={tea.cakeWeight}
        onSelect={handleAddToCartWithWeight}
        pricePerGram={pricePerGram}
      />
    </>
  )
}

